import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import DragIndicatorIcon from 'icons/drag-indicator.svg';
import TrashIcon from 'icons/ic-trash.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Tooltip from 'components/application/Tooltip';

export default function FieldOption({
  isAutoFocused,
  isDeletable,
  isInvalid,
  onOptionDelete,
  onOptionValueChange,
  option,
  removeErrorStyling,
}) {
  const { id, value } = option;

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.personnel.information.FieldOption',
  });

  return (
    <li
      className={classNames(
        'sortable-collection__member-wrapper tw-group tw-relative',
        isDragging && 'tw-z-[1]',
      )}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className='sortable-collection__member'>
        <div
          className={classNames(
            'tw-flex tw-h-9 tw-w-5 tw-touch-none tw-items-center tw-justify-center group-hover:tw-visible hover-hover:tw-invisible',
            isDragging ? 'tw-cursor-grabbing' : 'tw-cursor-grab',
          )}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon
            className='[&_path]:tw-fill-grey-700'
            height={12}
            width={8}
          />
        </div>
        <div className='sortable-collection__input-container'>
          <input
            autoFocus={isAutoFocused}
            className={classNames(
              'sortable-collection__input field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
              isInvalid ?
                'tw-border-red-600 focus-within:tw-border-red-600 hover:tw-border-red-600 hover:focus-within:tw-border-red-600'
              : 'tw-border-grey-300 placeholder:tw-text-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300',
            )}
            id={`sortable-collection__input-${id}`}
            onChange={(event) => {
              onOptionValueChange({ id, value: event.target.value });
            }}
            onFocus={removeErrorStyling}
            type='text'
            value={value}
          />
        </div>
        <div className='tw-my-2 tw-ml-3 tw-mr-5'>
          <div
            className={classNames(
              'tooltip-parent tw-inline-flex tw-h-6 tw-w-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-p-0',
              isDeletable ?
                'tw-bg-transparent hover:tw-bg-red-025 active:tw-bg-red-050 [&_svg_path]:tw-fill-grey-500 [&_svg_path]:hover:tw-fill-red-600 [&_svg_path]:active:tw-fill-red-600'
              : 'tw-pointer-events-none tw-bg-grey-050 [&_svg_path]:tw-fill-grey-300',
            )}
          >
            <TrashIcon
              className='sortable-collection__delete-button'
              height={24}
              onClick={isDeletable ? () => onOptionDelete({ id }) : null}
              width={24}
            />
            {!isDeletable && (
              <Tooltip
                className='tooltip-dark--max-w-xxs'
                placement='left'
                tooltip={i18nT('tooltip')}
                trigger='hover'
              />
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

FieldOption.propTypes = {
  isAutoFocused: PropTypes.bool.isRequired,
  isDeletable: PropTypes.bool.isRequired,
  isInvalid: PropTypes.bool.isRequired,
  onOptionDelete: PropTypes.func.isRequired,
  onOptionValueChange: PropTypes.func.isRequired,
  option: PropTypes.exact({
    id: PropTypes.string.isRequired,
    isNew: PropTypes.bool.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
};
